import axios from "axios";
import { API_Domain, CSRFTokenTimeLimit } from "../constants/api-paths";

/**
 * CSRF Token Management
 * Handles fetching, caching, and refreshing of CSRF tokens
 */

let csrfPromise: Promise<string> | null = null;
let csrfTokenCache: string | null = null;

/**
 * Cookie utility functions
 * Framework-agnostic cookie management
 */
export const getCookie = (name: string): string => {
  if (typeof document === "undefined") return "";

  const nameEQ = `${name}=`;
  const ca = document.cookie.split(";");

  for (const rawCookie of ca) {
    let c = rawCookie;
    while (c.startsWith(" ")) c = c.substring(1, c.length);
    if (c.startsWith(nameEQ)) return c.substring(nameEQ.length, c.length);
  }
  return "";
};

export const setCookie = (name: string, timeLimit: number, value: string): void => {
  if (typeof document === "undefined") return;

  const d = new Date();
  d.setTime(d.getTime() + timeLimit);
  const expires = `expires=${d.toUTCString()}`;
  document.cookie = `${name}=${value};${expires};path=/`;
};

export const deleteCookie = (name: string): void => {
  if (typeof document === "undefined") return;
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
};

/**
 * Get CSRF Token
 * Fetches a new CSRF token from the server
 *
 * @param isRetry - Whether this is a retry attempt (adds timestamp to bypass cache)
 * @returns Promise resolving to CSRF token
 */
export const getCSRF = async (isRetry = false): Promise<string> => {
  const tsParam = isRetry ? `?ts=${Date.now().toString()}` : "";
  const url = API_Domain ? `${API_Domain}/api/v2/csrf${tsParam}` : `/api/v2/csrf${tsParam}`;

  try {
    const res = await axios({
      method: "GET",
      url,
      withCredentials: true,
      headers: {
        "Content-Type": "application/json",
      },
    });

    const token = res?.data.data._csrf as string;
    setCookie("CSRFtoken", CSRFTokenTimeLimit, token);
    csrfTokenCache = token;
    return token;
  } catch (error) {
    console.error("Failed to fetch CSRF token:", error);
    throw error;
  }
};

/**
 * Wait for CSRF Token
 * Ensures only one CSRF request is made at a time
 *
 * @param isRetry - Whether this is a retry attempt
 * @returns Promise resolving to CSRF token
 */
export const waitForCSRF = (isRetry = false): Promise<string> => {
  csrfPromise ??= getCSRF(isRetry).then((token: string) => {
    csrfPromise = null;
    return token;
  });
  return csrfPromise;
};

/**
 * Get Cached CSRF Token
 * Returns cached CSRF token or fetches a new one
 *
 * @returns CSRF token string
 */
export const getCachedCSRF = async (): Promise<string> => {
  const cookieToken = getCookie("CSRFtoken");

  if (cookieToken) {
    csrfTokenCache = cookieToken;
    return cookieToken;
  }

  if (csrfTokenCache) {
    return csrfTokenCache;
  }

  return waitForCSRF();
};

/**
 * Refresh CSRF Token
 * Forces a refresh of the CSRF token
 *
 * @returns Promise resolving to new CSRF token
 */
export const refreshCSRF = async (): Promise<string> => {
  csrfTokenCache = null;
  deleteCookie("CSRFtoken");
  return waitForCSRF(true);
};

/**
 * Validate CSRF Token
 * Checks if current CSRF token is valid
 *
 * @returns boolean indicating if token is valid
 */
export const isCSRFValid = (): boolean => {
  const token = getCookie("CSRFtoken");
  return !!token;
};
